import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { en, type MessageKey } from './en';
import { zh } from './zh';

export type Locale = 'en' | 'zh';
export type { MessageKey };
export { formatDate, formatDateTime, parseDate } from './dates';

const STORAGE_KEY = 'aheadmetric.locale';
const DICTIONARIES: Record<Locale, Record<MessageKey, string>> = { en, zh };

export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'zh') return stored;
  } catch {
    // localStorage unavailable (private mode) — fall through to detection
  }
  const lang = typeof navigator !== 'undefined' ? navigator.language : '';
  return (lang ?? '').toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  children, initialLocale,
}: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
    return () => { document.documentElement.lang = 'en'; };
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // persistence is best-effort
    }
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}

export function useT() {
  const { locale } = useLocale();
  return useCallback(
    (key: MessageKey, params?: Record<string, string | number>) => {
      const text: string | undefined = DICTIONARIES[locale][key];
      let out = text ?? key;
      if (params) {
        for (const [name, val] of Object.entries(params)) {
          out = out.replaceAll(`{${name}}`, String(val));
        }
      }
      return out;
    },
    [locale],
  );
}
